import { ArrowLeftIcon, ArrowRightIcon, Check, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { BorderedRadioButton, Button, RadioGroup, TextLink, useBreakpoint } from 'slab'

import { emitByopEligibilityCheckPageViewEvent } from '../events'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'

type OffThatchMedicalDisplayProps = StepComponentProps & {
  setViewingOffThatch: (viewingOffThatch: boolean) => void
  onContinue: () => void
}

type PlanDecision = 'valid_plan' | 'keep_invalid_plan' | 'go_back'

export function AllowedOffThatchMedicalPlans ({
  navigateToSpecificStep,
  setViewingOffThatch,
  onContinue,
  userId,
}: OffThatchMedicalDisplayProps) {
  const [planDecision, setPlanDecision] = useState<PlanDecision | null>(null)
  const { onboardingData } = useOnboarding()
  const { breakpointIsAtLeast } = useBreakpoint()

  useEffect(() => {
    if (onboardingData && userId) {
      emitByopEligibilityCheckPageViewEvent(userId, onboardingData)
    }
  }, [onboardingData, userId])

  const onClickContinue = () => {
    if (planDecision === 'valid_plan') {
      onContinue()
    } else if (planDecision === 'keep_invalid_plan') {
      navigateToSpecificStep?.('opted_out')
    } else if (planDecision === 'go_back') {
      setViewingOffThatch(false)
    }
  }

  let continueButtonLabel = 'Continue'
  if (planDecision === 'keep_invalid_plan') {
    continueButtonLabel = 'Go to opt out'
  } else if (planDecision === 'go_back') {
    continueButtonLabel = 'Go back to Thatch plans'
  }

  const continueMessage = (
    <span className='body-sm text-gray-500'>
      By continuing, you acknowledge you'll have to give your allowance back if
      it's used on an unsupported kind of plan.
    </span>
  )

  const continueButton = (
    <Button
      size='large'
      variant='primary'
      rightIcon={ArrowRightIcon}
      onClick={onClickContinue}
      width={breakpointIsAtLeast('xs') ? 'auto' : 'full'}
    >
      {continueButtonLabel}
    </Button>
  )

  return (
    <div
      data-testid='OffThatchMedicalDisplay-allowedPlans'
      className='max-w-[44rem]'
    >
      <div className='mb-8 -ml-4 xs:-mt-[4.25rem]'>
        <Button
          leftIcon={ArrowLeftIcon}
          size='small'
          variant='tertiary-deemphasized'
          onClick={() => {
            setViewingOffThatch(false)
          }}
        >
          Back to all medical plans
        </Button>
      </div>
      <div className='mb-10'>
        <h1 className='heading-lg text-gray-800 mb-5'>Use my own plan</h1>
        <h2 className='heading-sm text-gray-600 mt-7 mb-5'>
          Make sure you have the right kind of coverage
        </h2>
        <p className='body text-gray-500'>
          Your employer allowance can only be used{' '}
          <TextLink
            target='_blank'
            href='https://support.thatch.com/eligible-medical-plans#eligible-plans-for-individuals-under-65-years-of-age'
          >
            for certain types of plans
          </TextLink>{' '}
          that you purchase yourself, like:
        </p>
        <ul className='list-none mt-2 pb-3 body'>
          <li className='flex items-center mb-2'>
            <Check className='w-4 h-4 mr-1 text-green-400 shrink-0' />
            Individual and family plans purchased without tax credits or
            subsidies
          </li>
          <li className='flex items-center mb-4'>
            <Check className='w-4 h-4 mr-1 text-green-400 shrink-0' />
            Student health insurance
          </li>
        </ul>
        <p className='body text-gray-500'>It can't be used for plans like:</p>
        <ul className='list-none mt-2 pb-3 body'>
          <li className='flex items-center mb-2 last:mb-0'>
            <X className='w-4 h-4 mr-1 text-red-400 shrink-0' />
            Individual and family plans purchased with tax credits or subsidies
          </li>
          <li className='flex items-center mb-2 last:mb-0'>
            <X className='w-4 h-4 mr-1 text-red-400 shrink-0' />
            Employer group plans, including COBRA
          </li>
          <li className='flex items-center mb-2 last:mb-0'>
            <X className='w-4 h-4 mr-1 text-red-400 shrink-0' />
            Government-sponsored plans, like Medicaid, TRICARE, or Veterans Affairs (VA)
          </li>
        </ul>
        <div className='pt-2'>
          <RadioGroup gap={3} name='plan-decision'>
            <BorderedRadioButton
              label='I have the right kind of plan'
              value='valid_plan'
              checked={planDecision === 'valid_plan'}
              onChange={() => setPlanDecision('valid_plan')}
            />
            <BorderedRadioButton
              label="Never mind, I'd prefer to get a plan from Thatch"
              value='go_back'
              checked={planDecision === 'go_back'}
              onChange={() => setPlanDecision('go_back')}
            />
            <BorderedRadioButton
              label="I have an invalid plan that I'd like to keep and will forgo my allowance"
              value='keep_invalid_plan'
              checked={planDecision === 'keep_invalid_plan'}
              onChange={() => setPlanDecision('keep_invalid_plan')}
            />
          </RadioGroup>
        </div>
        {planDecision === 'valid_plan' && (
          <div className='mt-8 flex flex-col xs:flex-row gap-4 xs:items-center'>
            {breakpointIsAtLeast('xs') && continueMessage}
            <div className='justify-end'>{continueButton}</div>
            {!breakpointIsAtLeast('xs') && continueMessage}
          </div>
        )}
        {planDecision && planDecision !== 'valid_plan' && (
          <div className='mt-8 flex justify-end'>{continueButton}</div>
        )}
      </div>
    </div>
  )
}
